import React, { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Button, Card, CardActions, CardContent, Grid, InputAdornment, Link, TextField } from '@mui/material';
import CurrencyFormatField from 'components/common/CurrencyFormatField';
import { TransportationModel, TransportationStatus } from 'models/TransportationModel';
import { CompanyType } from 'models/CompanyModel';
import Directions from 'modules/transportation/components/Common';
import auth from 'recoil/atoms/auth';
import Api from 'utils/api';
import { forceDownload } from 'utils/common';


const DeliveredInner = () => {
    const [transportations, setTransportations] = useState<TransportationModel[]>([]);

    const load = () => {
        Api.transportation
            .all(TransportationStatus.Delivered)
            .then(res => setTransportations(res.data));
    };

    useEffect(load, []);

    const groups = transportations.reduce((ret, a) => {
        const key = a.orderId;
        return {...ret, [key]: ret[key] ? [...ret[key], a] : [a]}
    }, {} as Record<string, TransportationModel[]>);

    return (
        <>
            {Object.values(groups).map((group, i) => <TransportGroup
                key={i}
                group={group}
                load={load}
            />)}
        </>
    )
};

type TransportGroupProps = {
    group: TransportationModel[],
    load: () => void,
}

const TransportGroup = ({group, load}: TransportGroupProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const first = group[0];
    const [amount, setAmount] = useState<number>(first.order.price);

    const setPrice = () => amount && Api.transportation
        .setPrice(first.orderId, amount)
        .then(load);

    const download = () => Api.transportation
        .downloadOrder(first.orderId)
        .then(forceDownload);

    return (
        <Card sx={{
            mb: 2,
        }}>
            <CardContent sx={{bgcolor: 'grey.200'}}>
                <Directions transportation={first}/>
            </CardContent>

            <CardContent>
                {group.map(transportation => (
                    <Grid key={transportation.id} container>
                        <Grid item xs={4}>
                            {my.type === CompanyType.Master ?
                                <Link href={`/car/${transportation.car.id}`}>{transportation.car.chassisNumber}</Link>:
                                <>{transportation.car.chassisNumber}</>
                            }
                        </Grid>
                        <Grid item xs={4} sx={{fontSize: {xs: '.75rem', sm: '1rem'}}}>
                            {transportation.car.make} {transportation.car.model}
                        </Grid>
                        <Grid item xs={4}>
                            {transportation.car.company.name}
                        </Grid>
                    </Grid>
                ))}
            </CardContent>

            <CardContent>
                <TextField
                    id="price"
                    label={t('price')}
                    fullWidth
                    value={amount}
                    onChange={e => setAmount(Number(e.target.value))}
                    InputProps={{
                        inputComponent: CurrencyFormatField as any,
                        startAdornment: <InputAdornment position='start'>¥</InputAdornment>,
                        endAdornment: (
                            <Button
                                sx={{whiteSpace: 'nowrap', padding: '.5rem 1.5rem'}}
                                onClick={setPrice}
                            >
                                {t('set_price')}
                            </Button>
                        )
                    }}
                />
            </CardContent>

            <CardActions sx={{justifyContent: 'space-between'}}>
                <Button onClick={download} size="small">{t('download')}</Button>
            </CardActions>
        </Card>
    )
}

const Delivered = () => {
    return (
        <Suspense fallback={null}>
            <DeliveredInner/>
        </Suspense>
    )
};

export default Delivered;
